/// <amd-module name="Wiki/Overview/Stores/OverviewStore" />
import { announce } from "VSS/Core/Util/Accessibility";
import { Store } from "VSS/Reflux/Store";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { PageInfoLoadedPayload, PageReorderedPayload, PageSavedPayload, WikiPagesFetchedPayload } from "Wiki/Overview/Actions/ActionCreatorPayloads";
import { ErrorState, WikiPageInfo, WikiPageInfoWithName, WikiViewOptions } from "Wiki/ViewCommon/Contracts";

export interface INavPaneState {
    isCollapsed: boolean;
    currentSize: number;
}
export interface IOverviewState {
    anchor?: string;
    isPageInfoLoading: boolean;
    pageInfo?: WikiPageInfo; // contains currently selected saved page.
    tempPageInfo?: WikiPageInfoWithName; // Used to store unsaved page e.g: on add new page.
    isEditing?: boolean;
    isEditorModuleLoaded: boolean;
    navPaneState: INavPaneState;
    // isPageUnfollow is set when in url we get unfollow=true to unfollow the wiki page
    isPageUnfollow?: boolean;
    // refreshFollowStatus is set when a new page is added.
    // This is send to the followComponent, so that follow status can be
    // refreshed in cache to accomodate newly added follower on page Add
    refreshFollowStatus?: boolean;
    errorState?: ErrorState;
}

const NavPaneInitialSize = 320;

export class OverviewStore extends Store {
    private _state: IOverviewState;

    constructor(wikiViewOptions: WikiViewOptions, wikiPageInfo?: WikiPageInfo) {
        super("OverviewStore");

        this._state = {
            anchor: wikiViewOptions.anchor,
            isPageUnfollow: wikiViewOptions.unfollow,
            pageInfo: wikiPageInfo,
            isPageInfoLoading: false,
            isEditorModuleLoaded: false,
            navPaneState: {
                isCollapsed: false,
                currentSize: NavPaneInitialSize
            }
        };
    }

    public getState = (): IOverviewState => {
        return this._state;
    };

    public editorModuleLoaded = (): void => {
        this._state.isEditorModuleLoaded = true;
        this.emitChanged();
    };

    public startEditingNewPage = (payload: WikiPageInfoWithName): void => {
        this._state.tempPageInfo = payload;
        this._startEditing();
    };

    public startEditing = (payload: WikiPageInfo): void => {
        this._state.pageInfo = payload;
        this._startEditing();
    };

    public startEditingCurrentPage = (): void => {
        this._startEditing();
    };

    public closeEditor = (): void => {
        this._state.isEditing = false;
        this._state.tempPageInfo = undefined;
        this._state.navPaneState.isCollapsed = false;
        this._state.anchor = undefined;
        this.emitChanged();
    };

    public onAnchorClicked = (anchor: string): void => {
        this._state.anchor = anchor;
        this.emitChanged();
    };

    public onAnchorRemoved = (): void => {
        this._state.anchor = undefined;
        this.emitChanged();
    };

    public onPageSelected = (wikiPage?: WikiPage, anchor?: string, isSystemAction?: boolean): void => {
        this._state.pageInfo = wikiPage && {
            ...wikiPage,
            version: undefined // till we load content the version is undefined
        };
        // don't reset error, if it is system triggered action
        if (!isSystemAction) {
            this._state.errorState = undefined;
        }
        this._state.anchor = anchor;
        this._state.isPageInfoLoading = true;
        this.emitChanged();
    };

    public onPageInfoLoaded = (payload: PageInfoLoadedPayload): void => {
        // don't reset error, if it is system triggered action
        if (!payload.isSystemAction) {
            this._state.errorState = undefined;
        }
        this._state.pageInfo = payload.wikiPageInfo;
        this._state.isPageInfoLoading = false;
        this.emitChanged();
    };

    public onPageReordered = (payload: PageReorderedPayload): void => {
        this.updateError({ message: payload.errorMessage, buttonProps: payload.errorButtonProps });
    };

    public savePage = (payload: PageSavedPayload): void => {
        this._state.pageInfo = {
            ...payload.page,
            version: payload.pageVersion
        };
        this._state.refreshFollowStatus = true;
        this._state.tempPageInfo = undefined;
        if (payload.closeEditor) {
            this.closeEditor();
        } else {
            this.emitChanged();
        }
    };

    public updateNavPaneSize = (newSize: number): void => {
        this._state.navPaneState.currentSize = newSize;

        // size changed implies, it's no longer collapsed.
        this._state.navPaneState.isCollapsed = false;
        this.emitChanged();
    };

    public updateNavPaneVisibility = (isCollapsed: boolean): void => {
        this._state.navPaneState.isCollapsed = isCollapsed;
        this.emitChanged();
    };

    public onUnsetUnfollowPage = (): void => {
        this._state.isPageUnfollow = false;
    };

    public onRefreshFollowStatus = (status: boolean): void => {
        this._state.refreshFollowStatus = status;
    };

    public updateError = (pageErrorPayload?: ErrorState): void => {
        this._state.errorState = pageErrorPayload || {};
        this.emitChanged();
        if (this._state.errorState.message) {
            announce(this._state.errorState.message);
        }
    };

    private _startEditing = (): void => {
        this._state.isEditing = true;
        this._state.errorState = undefined;
        this._state.navPaneState.isCollapsed = true;
        this.emitChanged();
    };
}
